/**
 * Main App component for Listing Photos extension
 * 
 * Renders the full photo management UI in Monday.com.
 * Supports two modes:
 * - Item View: Photos for a specific listing
 * - Board View: Brand assets (generic images not tied to a listing)
 */

import { useEffect, useState, useMemo } from 'react';
import { useMondayContext } from './hooks/useMondayContext';
import { useImages } from './hooks/useImages';
import { ImageGrid } from './components/ImageGrid';
import { ErrorBanner } from './components/ErrorBanner';
import { DescriptionStatusBanner } from './components/DescriptionStatusBanner';
import { DeleteModal } from './components/DeleteModal';
import { ImageLightbox } from './components/ImageLightbox';

export function App() {
  const { itemId, isBrandMode, loading: contextLoading, error: contextError } = useMondayContext();
  const {
    images,
    autoHeroId,
    loading: imagesLoading,
    error: imagesError,
    failedUploads,
    uploadProgress,
    loadImages,
    uploadFiles,
    reorderImages,
    deleteImage,
    setHero,
    clearFailedUploads,
    retryFailedUploads,
  } = useImages();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Copy URL feedback
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  // Load images when item ID is available
  useEffect(() => {
    if (itemId) {
      loadImages(itemId);
    }
  }, [itemId, loadImages]);

  // Calculate if any images are generating (for polling)
  const hasGeneratingImages = useMemo(
    () => images.some((img) => img.description_status === 'generating'),
    [images]
  );

  // Poll for updates while uploading or generating descriptions
  const isUploading = !!uploadProgress;
  useEffect(() => {
    if (!itemId || (!hasGeneratingImages && !isUploading)) {
      return;
    }

    const pollInterval = setInterval(() => {
      loadImages(itemId);
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [itemId, hasGeneratingImages, isUploading, loadImages]);

  // Handle upload
  const handleUpload = async (files: File[]) => {
    if (itemId) {
      await uploadFiles(itemId, files);
    }
  };

  // Handle reorder
  const handleReorder = async (newOrder: string[]) => {
    if (itemId) {
      await reorderImages(itemId, newOrder);
    }
  };

  // Handle set hero
  const handleSetHero = async (imageId: string) => {
    if (itemId) {
      await setHero(itemId, imageId);
    }
  };

  // Handle delete
  const handleDeleteClick = (imageId: string) => {
    setImageToDelete(imageId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (imageToDelete) {
      setIsDeleting(true);
      try {
        await deleteImage(imageToDelete);
      } finally {
        setIsDeleting(false);
      }
    }
    setDeleteModalOpen(false);
    setImageToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setImageToDelete(null);
  };

  // Handle copy URL with feedback
  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback('URL copied!');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  };

  // Handle retry failed uploads
  const handleRetry = () => {
    if (itemId) {
      retryFailedUploads(itemId);
    }
  };

  // Handle image click to open lightbox
  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Loading state
  if (contextLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        Loading...
      </div>
    );
  }

  // Context error
  if (contextError) {
    return (
      <div
        style={{
          padding: '16px',
          textAlign: 'center',
          color: '#dc2626',
          fontSize: '13px',
        }}
      >
        {contextError}
      </div>
    );
  }

  // Main Item View UI
  return (
    <div style={{ padding: '16px', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h1 style={{ 
          margin: 0, 
          fontSize: '18px', 
          fontWeight: 600, 
          color: '#ffffff',
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
        }}>
          {isBrandMode ? 'Brand Assets' : 'Listing Photos'}
        </h1>
        <button
          onClick={() => itemId && loadImages(itemId)}
          disabled={imagesLoading}
          style={{
            padding: '6px 12px',
            backgroundColor: '#f3f4f6',
            border: 'none',
            borderRadius: '6px',
            cursor: imagesLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          Refresh
        </button>
      </div>

      {/* Upload progress bar */}
      {uploadProgress && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '14px',
              color: '#1e40af',
            }}
          >
            <span>Uploading...</span>
            <span>{uploadProgress.current} of {uploadProgress.total}</span>
          </div>
          <div
            style={{
              height: '6px',
              backgroundColor: '#dbeafe',
              borderRadius: '3px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                backgroundColor: '#3b82f6',
                borderRadius: '3px',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Error banner for failed uploads */}
      <ErrorBanner
        failedUploads={failedUploads}
        onRetry={handleRetry}
        onDismiss={clearFailedUploads}
      />

      {/* Description generation status banner */}
      <DescriptionStatusBanner images={images} />

      {/* API error */}
      {imagesError && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            marginBottom: '16px',
            color: '#991b1b',
            fontSize: '14px',
          }}
        >
          {imagesError}
        </div>
      )}

      {/* Loading images */}
      {imagesLoading && images.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
          Loading images...
        </div>
      ) : (
        <>
          {/* Image grid */}
          <ImageGrid
            images={images}
            autoHeroId={autoHeroId}
            onReorder={handleReorder}
            onUpload={handleUpload}
            onDelete={handleDeleteClick}
            onSetHero={handleSetHero}
            onCopyUrl={handleCopyUrl}
            onImageClick={handleImageClick}
            disabled={imagesLoading || !!uploadProgress}
          />

          {/* Image count */}
          <div
            style={{
              marginTop: '16px',
              fontSize: '13px',
              color: '#6b7280',
            }}
          >
            {images.length} {isBrandMode ? 'asset' : 'image'}{images.length !== 1 ? 's' : ''}
          </div>
        </>
      )}

      {/* Delete confirmation modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Image lightbox */}
      <ImageLightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={setLightboxIndex}
      />

      {/* Copy URL feedback toast */}
      {copyFeedback && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: copyFeedback === 'URL copied!' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
          }}
        >
          {copyFeedback}
        </div>
      )}
    </div>
  );
}
